import React, { FC, useState } from 'react';

import { Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import AdminTableListPage from 'components/admin/AdminTableListPage';
import { InputV2 } from 'components/common/ui-kit/Input/view';
import Loader from 'components/common/ui-kit/Loader';
import ReactSelect, {
  MultiValue,
  Option,
} from 'components/common/ui-kit/Select/view';
import {
  columnsCoordinatorStatements,
  columnsExpertStatements,
  columnsStatements,
} from 'config/moderator-table';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import { useApplicationFilterOptions } from 'hooks/moderator/applications/useApplicationFilterOptions';
import { useInitPageFilters } from 'hooks/moderator/useInitFilters';
import terms from 'i18n';
import {
  ApplicationChecklistState,
  OriginalDocumentSubmissionStatus,
  TypeOfCompetitionGroup,
} from 'models/applications/applications';
import {
  ApplicationFlag,
  ApplicationState,
  MessagesState,
} from 'models/applications/enum';
import { SelectOption } from 'models/common';
import { UserRole } from 'models/user';
import { createToast } from 'redux/actions';
import { useGetCountriesQuery } from 'redux/api/common/enum-api';
import {
  staffApplicationsApi,
  useLazyGetAllApplicationsAsCSVQuery,
  useLazyNextApplicationQuery,
} from 'redux/api/staff/staff-applications-api';
import { selectIsUserSupport, selectUserRole } from 'redux/selectors';
import { applicationsManagerClient } from 'utils/axios-clients';
import { downloadFile, uploadFile } from 'utils/common';
import { createInputV2Props } from 'utils/form-helpers';

import { isValidSnils, normalizeSnils } from '../../../utils/form-validators';

export const mapApplicationStateToText = (
  state?: ApplicationState,
  flags?: ApplicationFlag[]
) => {
  if (!state || !flags) return '';

  return flags.includes(ApplicationFlag.EDITED_BY_USER) &&
    state === ApplicationState.IN_WORK
    ? terms.APPLICATION_EDITED_BY_USER
    : terms[state];
};

export const mapFiltersToRequest = (filters, map) => {
  const request = {
    ...filters,
  };

  for (const filter in request) {
    if (Array.isArray(request[filter]) && request[filter].length === 0) {
      delete request[filter];
    } else if (request[filter] === '') {
      delete request[filter];
    }
  }

  if (
    request.applicationEducationLevel &&
    Array.isArray(request.applicationEducationLevel)
  ) {
    const hasPreparatoryCurrYear = request.applicationEducationLevel.some(
      item => item.value === 'PREPATORY_CURRENT_YEAR'
    );
    const hasPreparatory = request.applicationEducationLevel.some(
      item => item.value === 'PREPARATORY'
    );

    request.applicationEducationLevel = request.applicationEducationLevel
      .map(item =>
        item.value === 'PREPATORY_CURRENT_YEAR'
          ? { value: 'PREPARATORY' }
          : item
      )
      .filter(
        (item, index, self) =>
          self.findIndex(t => t.value === item.value) === index
      );

    if (hasPreparatoryCurrYear !== hasPreparatory) {
      request.applyingThisYear = hasPreparatoryCurrYear;
    } else {
      delete request.applyingThisYear;
    }
  }

  request.applicationState = filters.applicationState?.map(stateOption => {
    if (stateOption.value === ApplicationFlag.EDITED_BY_USER) {
      request.stateEditedByUser = true;
      return { ...stateOption, value: ApplicationState.IN_WORK };
    }
    return stateOption;
  });

  if (filters.unreadMessages) {
    request.unreadMessages =
      filters.unreadMessages.value === MessagesState.UNREAD;
  }

  return map(request);
};

export const exportAllUsersSchoolTests = () => {
  // Запрос через отдельный клиент, чтобы избежать кэширования rtk-query
  return applicationsManagerClient
    .post(
      '/school-tests/export-all-users',
      {},
      {
        responseType: 'blob',
      }
    )
    .then(res => {
      downloadFile(new File([res.data], 'users.zip'));
    });
};

export const exportNonConfirmedUsersSchoolTests = () => {
  // Запрос через отдельный клиент, чтобы избежать кэширования rtk-query
  return applicationsManagerClient
    .post(
      '/school-tests/export-non-confirmed-users',
      {},
      {
        responseType: 'blob',
      }
    )
    .then(res => {
      downloadFile(new File([res.data], 'users.zip'));
    });
};

export const exportUserSchoolTests = () => {
  // Запрос через отдельный клиент, чтобы избежать кэширования rtk-query
  return applicationsManagerClient
    .post(
      '/school-tests/export-students',
      {
        type: 'ALL',
        filters: {},
      },
      {
        responseType: 'blob',
      }
    )
    .then(res => {
      downloadFile(new File([res.data], 'users.zip'));
    });
};

export const importUserSchoolTests = (
  onSuccess?: () => void,
  onError?: () => void,
  userId?: number
) => {
  uploadFile(files => {
    const file = files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    applicationsManagerClient
      .post('/school-tests/parse', formData, {
        params: {
          userId,
        },
      })
      .then(onSuccess)
      .catch(onError);
  }, 'text/csv');
};

export const ModeratorStatementsFilter = ({
  control,
  formState: { errors },
  setValue,
  setTrajectory,
  setEducationLevel,
}) => {
  const inputV2Props = createInputV2Props(errors);
  const visibleFilters =
    useAppSelector(state => state.visibleFilters.applications) ?? [];
  const {
    educationFormSelectProps,
    paymentFormSelectProps,
    facultiesFormSelectProps,
    countrySelectProps,
    applicationStatusSelectProps,
    expertStatusSelectProps,
    coordinatorStatusSelectProps,
    messagesStateSelectProps,
    recruitersSelectProps,
    educationLevelSelectProps,
    trajectorySelectProps,
    educationProgramsSelectProps,
    applicationAdmissionConditionSelectProps,
    moderatorSelectProps,
    serveMethodProps,
    applicationEpguStatusSelectProps,
    uploadStatusSelectProps,
  } = useApplicationFilterOptions();

  const isUserSupport = useAppSelector(selectIsUserSupport);

  return (
    <>
      {visibleFilters.includes('userId') && (
        <Controller
          name="userId"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="ID СПБПУ / ID ЕПГУ абитуриента"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('lastName') && (
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Фамилия"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('firstName') && (
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Имя"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('middleName') && (
        <Controller
          name="middleName"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Отчество"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('email') && (
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Email"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('snils') && (
        <Controller
          name="snils"
          control={control}
          rules={{
            validate: value => {
              return value && value.length >= 14
                ? isValidSnils(normalizeSnils(value))
                  ? true
                  : terms.SNILS_INVALID
                : true;
            },
            onChange: ({ target: { value } }) =>
              value && setValue('snils', normalizeSnils(value)),
          }}
          render={({ field }) => (
            <InputV2
              placeholder="СНИЛС"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('directionPaymentFormId') && (
        <Controller
          name="directionPaymentFormId"
          control={control}
          render={({ field }) => (
            <ReactSelect
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={paymentFormSelectProps.options}
              placeholder="Основания поступления"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('directionEducationFormId') && (
        <Controller
          name="directionEducationFormId"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              placeholder={terms.MODE_OF_STUDY}
              options={educationFormSelectProps.options}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('citizenshipIds') && (
        <Controller
          name="citizenshipIds"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              placeholder={terms.CITIZENSHIP}
              className="filter-form__field"
              {...(field as object)}
              options={countrySelectProps.options}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {!isUserSupport && (
        <>
          {visibleFilters.includes('applicationState') && (
            <Controller
              name="applicationState"
              control={control}
              render={({ field }) => (
                <ReactSelect<SelectOption, true>
                  isMulti
                  styles={{
                    valueContainer: provided => ({
                      ...provided,
                      maxHeight: '30px',
                      overflowY: 'auto',
                    }),
                    menu: base => ({ ...base, minWidth: '300px' }),
                  }}
                  className="filter-form__field"
                  {...(field as object)}
                  placeholder="Статус заявления"
                  options={applicationStatusSelectProps.options}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  allowSelectAll={true}
                  components={{ Option, MultiValue }}
                />
              )}
            />
          )}

          {visibleFilters.includes('applicationExpertStatus') && (
            <Controller
              name="applicationExpertStatus"
              control={control}
              render={({ field }) => (
                <ReactSelect<SelectOption, true>
                  isMulti
                  styles={{
                    valueContainer: provided => ({
                      ...provided,
                      maxHeight: '30px',
                      overflowY: 'auto',
                    }),
                    menu: base => ({ ...base, minWidth: '300px' }),
                  }}
                  className="filter-form__field"
                  {...(field as object)}
                  options={expertStatusSelectProps.options}
                  placeholder="Статус эксперта"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  allowSelectAll={true}
                  components={{ Option, MultiValue }}
                />
              )}
            />
          )}
          {visibleFilters.includes('applicationCoordinatorStatus') && (
            <Controller
              name="applicationCoordinatorStatus"
              control={control}
              render={({ field }) => (
                <ReactSelect<SelectOption, true>
                  isMulti
                  styles={{
                    valueContainer: provided => ({
                      ...provided,
                      maxHeight: '30px',
                      overflowY: 'auto',
                    }),
                    menu: base => ({ ...base, minWidth: '300px' }),
                  }}
                  className="filter-form__field"
                  {...(field as object)}
                  options={coordinatorStatusSelectProps.options}
                  placeholder="Статус координатора"
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  allowSelectAll={true}
                  components={{ Option, MultiValue }}
                />
              )}
            />
          )}
        </>
      )}

      {visibleFilters.includes('unreadMessages') && (
        <Controller
          name="unreadMessages"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={messagesStateSelectProps.options}
              placeholder="Непрочитанные сообщения"
              closeMenuOnSelect={true}
              hideSelectedOptions={false}
              isClearable={true}
              // allowSelectAll={true}
              // components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('directionName') && (
        <Controller
          name="directionName"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Направление"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('facultyIds') && (
        <Controller
          name="facultyIds"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={facultiesFormSelectProps.options}
              placeholder="Подразделение"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
              // TODO
            />
          )}
        />
      )}

      {visibleFilters.includes('recruitedIds') && (
        <Controller
          name="recruitedIds"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={recruitersSelectProps}
              placeholder="Партнер"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('applicationEducationLevel') && (
        <Controller
          name="applicationEducationLevel"
          control={control}
          rules={{
            onChange: ({ target: value }) => {
              value &&
                setEducationLevel(
                  value.value.map(educationLevel => educationLevel.value)
                );
            },
          }}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={educationLevelSelectProps.options}
              // todo: справочник 1С?
              placeholder="Уровень подготовки"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
              // onChange={list => {
              //   setEducationLevel(
              //     list.map(educationLevel => educationLevel.value)
              //   );
              // }}
            />
          )}
        />
      )}

      {visibleFilters.includes('applicationTrajectory') && (
        <Controller
          name="applicationTrajectory"
          control={control}
          rules={{
            onChange: ({ target: value }) => {
              value &&
                setTrajectory(value.value.map(trajectory => trajectory.value));
            },
          }}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={trajectorySelectProps.options}
              placeholder="Траектория поступления"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('serveMethods') && (
        <Controller
          name="serveMethods"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              placeholder="Способ подачи"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option, MultiValue }}
              options={serveMethodProps.options}
            />
          )}
        />
      )}

      {visibleFilters.includes('educationProgram') && (
        <Controller
          name="educationProgram"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={educationProgramsSelectProps.options}
              placeholder="Программа"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('creationDate') && (
        <Controller
          name="creationDate"
          control={control}
          render={({ field }) => (
            <InputV2
              placeholder="Дата подачи заявления"
              type="date"
              className="filter-form__field"
              {...inputV2Props(field)}
            />
          )}
        />
      )}

      {visibleFilters.includes('moderatorIds') && (
        <Controller
          name="moderatorIds"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={moderatorSelectProps.options}
              placeholder="Сотрудник ПК"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('applicationAdmissionCondition') && (
        <Controller
          name="applicationAdmissionCondition"
          control={control}
          render={({ field }) => (
            <ReactSelect
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={applicationAdmissionConditionSelectProps.options}
              placeholder="Особенности приема"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('uploadStatus') && (
        <Controller
          name="uploadStatus"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={uploadStatusSelectProps.options}
              placeholder="Состояние выгрузки в 1С"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}

      {visibleFilters.includes('applicationChecklistState') && (
        <Controller
          name="applicationChecklistState"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={[
                {
                  value: ApplicationChecklistState.NOT_CHECKED,
                  label: 'Есть непроверенный раздел',
                },
                {
                  value: ApplicationChecklistState.EDU_DOC_NOT_CHECKED,
                  label: 'Не подтверждены сведения об образовании',
                },
                {
                  value: ApplicationChecklistState.SCHOOL_TEST_NOT_CONFIRMED,
                  label: 'Есть непроверенные ЕГЭ',
                },
                {
                  value: ApplicationChecklistState.ENTRANCE_TEST_BOOK_MISSING,
                  label: 'Отсутствует запись на ВИ',
                },
                {
                  value: ApplicationChecklistState.GOVERNMENT_CONTRACT_MISSING,
                  label: 'Не подтверждена заявка на целевое обучение',
                },
                {
                  value: ApplicationChecklistState.ENTRANCE_TEST_NOT_SATISFIED,
                  label: 'Несоответствие ВИ выбранным КГ',
                },
                {
                  value: ApplicationChecklistState.BENEFIT_MISSING,
                  label: 'Отсутствует льгота для КГ',
                },
                {
                  value: ApplicationChecklistState.NOT_UPLOADED_DIRECTION,
                  label: 'Есть невыгруженная КГ в 1С',
                },
                {
                  value: ApplicationChecklistState.NOT_CONFIRMED_GENERAL_DATA,
                  label: 'Не подтверждены основные данные',
                },
                {
                  value: ApplicationChecklistState.NOT_CONFIRMED_DIRECTIONS,
                  label: 'Не подтверждены направления поступления',
                },
                {
                  value:
                    ApplicationChecklistState.NOT_CHECKED_INDIVIDUAL_ACHIEVEMENTS,
                  label: 'Есть непроверенные ИД',
                },
                {
                  value:
                    ApplicationChecklistState.NOT_CONFIRMED_INDIVIDUAL_ACHIEVEMENTS,
                  label: 'Есть неподтвержденные ИД',
                },
                {
                  value: ApplicationChecklistState.NOT_CHECKED_BENEFITS,
                  label: 'Есть непроверенные льготы',
                },
                {
                  value: ApplicationChecklistState.NOT_CONFIRMED_BENEFITS,
                  label: 'Есть неподтвержденные льготы',
                },
                {
                  value: ApplicationChecklistState.NOT_CHECKED_OLYMPIADS,
                  label: 'Есть непроверенные олимпиады',
                },
                {
                  value: ApplicationChecklistState.NOT_CONFIRMED_OLYMPIADS,
                  label: 'Есть неподтвержденные олимпиады',
                },
              ]}
              placeholder="Состояние чек-листа заявления"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              allowSelectAll={true}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}
      {visibleFilters.includes('originalDocumentSubmissionStatus') && (
        <Controller
          name="originalDocumentSubmissionStatus"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={[
                {
                  value: OriginalDocumentSubmissionStatus.PHYSICAL,
                  label: 'Бумажный вариант',
                },
                {
                  value: OriginalDocumentSubmissionStatus.ELECTRONIC_COPY,
                  label: 'Электронный вариант',
                },
                {
                  value: OriginalDocumentSubmissionStatus.NO_DOCUMENT,
                  label: 'Отсутствует',
                },
              ]}
              placeholder="Наличие согласия на зачисление / оригинала ДОО"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}
      {visibleFilters.includes('onlyForForeigners') && (
        <Controller
          name="onlyForForeigners"
          control={control}
          render={({ field }) => (
            <ReactSelect<SelectOption, true>
              isMulti
              styles={{
                valueContainer: provided => ({
                  ...provided,
                  maxHeight: '30px',
                  overflowY: 'auto',
                }),
                menu: base => ({ ...base, minWidth: '300px' }),
              }}
              className="filter-form__field"
              {...(field as object)}
              options={[
                {
                  value: TypeOfCompetitionGroup.ONLY_FOREIGN,
                  label: 'Только для ИГ',
                },
                {
                  value: TypeOfCompetitionGroup.ONLY_RUSSIAN,
                  label: 'Только для РФ',
                },
                {
                  value: TypeOfCompetitionGroup.MIXED,
                  label: 'Для РФ и ИГ',
                },
              ]}
              placeholder="Вид конкурсных групп"
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{ Option, MultiValue }}
            />
          )}
        />
      )}
    </>
  );
};

export const renderStatementsFilter = props => {
  return <ModeratorStatementsFilter {...props} />;
};
const ModeratorStatementsPage: FC = () => {
  const role = useAppSelector(selectUserRole);
  const { data: countries } = useGetCountriesQuery();
  const {
    moderatorSelectProps: { options: moderators },
    expertsSelectProps: { options: experts },
    coordinatorsSelectProps: { options: coordinators },
    recruitersSelectProps: recruiters,
  } = useApplicationFilterOptions();
  const [nextApplication] = useLazyNextApplicationQuery({});
  const [downloadAsCSV] = useLazyGetAllApplicationsAsCSVQuery();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [trajectory, setTrajectory] = useState();
  const [educationLevel, setEducationLevel] = useState();
  useInitPageFilters('applications');

  if (!countries) {
    return <Loader />;
  }

  const handleExportUserSchoolTests = () => {
    exportUserSchoolTests().catch(() => {
      dispatch(
        createToast(
          'Во время скачивания файлов проверки ЕГЭ произошла ошибка',
          'danger'
        )
      );
    });
  };

  const handleImportUserSchoolTests = () => {
    importUserSchoolTests(undefined, () => {
      dispatch(
        createToast(
          'Во время загрузки результатов ЕГЭ  произошла ошибка',
          'danger'
        )
      );
    });
  };

  const handleNextApplication = () => {
    nextApplication({
      trajectory: trajectory,
      educationLevel: educationLevel,
    })
      .unwrap()
      .then(result => {
        if (result) {
          window.open(`${pathname}/${result.id}`, '_blank');
          return;
        }
        dispatch(createToast('Новых заявлений для проверки нет', 'danger'));
      });
  };

  const extraButtons =
    role !== UserRole.ADMIN
      ? []
      : [
          {
            label: 'Только с паспортом РФ',
            onClick: exportAllUsersSchoolTests,
          },
          {
            label: 'Только непроверенные ЕГЭ',
            onClick: exportNonConfirmedUsersSchoolTests,
          },
          {
            label: 'Скачать файл проверки ЕГЭ',
            onClick: handleExportUserSchoolTests,
          },
          {
            label: 'Загрузить результаты ЕГЭ',
            onClick: handleImportUserSchoolTests,
          },
        ];

  (role === UserRole.MODERATOR || role === UserRole.ADMIN) &&
    extraButtons.push({
      label: 'Следующее заявление',
      onClick: handleNextApplication,
    });

  return (
    <>
      <AdminTableListPage
        storeKey="applications"
        title="Заявления"
        query={{
          use: staffApplicationsApi.useGetApplicationsByStaffQuery,
          mapQueryResultToData: data =>
            data.map(application => ({
              ...application,
              id: application?.id,
              prettyUserId:
                application?.userIdWithPrefix +
                ' / ' +
                (application?.epguInfo?.epguUniqueCodeProfile
                  ? application?.epguInfo?.epguUniqueCodeProfile
                  : '-'),
              faculty: application.faculty?.titleShort,
              directionEducationForm: application.directionEducationForm?.title,
              nationality: countries.find(
                country => country.id === application?.citizenshipId
              )?.title,
              educationForm: application.directionEducationForm?.title,
              formPayment: application.directionPaymentForm?.title,
              moderator: moderators.find(
                m => m.value === application.moderatorId
              )?.label,
              expert: experts.find(m => m.value === application.expertId)
                ?.label,
              coordinator: coordinators.find(
                m => m.value === application.coordinatorId
              )?.label,
              recruiter: recruiters.find(
                m => m.value === application.recruiterId
              )?.label,
              formattedApplicationId: application.formattedId,
              stateText: mapApplicationStateToText(
                application.state,
                application.flags
              ),
            })),
        }}
        displayCountLabel="Заявлений"
        columns={
          role === UserRole.EXPERT
            ? columnsExpertStatements
            : role === UserRole.COORDINATOR
            ? columnsCoordinatorStatements
            : columnsStatements
        }
        filtersConfig={{
          defaultValues: {
            userId: '',
            lastName: '',
            firstName: '',
            middleName: '',
            directionPaymentFormId: undefined,
            directionEducationFormId: undefined,
            citizenshipIds: undefined,
            applicationState: undefined,
            applicationExpertStatus: undefined,
            applicationCoordinatorStatus: undefined,
            unreadMessages: undefined,
            directionName: '',
            facultyIds: undefined,
            recruitedIds: undefined,
            applicationEducationLevel: undefined,
            applicationTrajectory: undefined,
            educationProgram: undefined,
            creationDate: undefined,
            moderatorIds: undefined,
            hasUndefinedFields: undefined,
          },
          renderFilters: props =>
            renderStatementsFilter({
              ...props,
              setTrajectory,
              setEducationLevel,
            }),
          mapFiltersToRequest: mapFiltersToRequest,
          onDownloadCsvButtonClick: ({ sorting, request }) =>
            downloadAsCSV({
              orderBy: sorting?.name,
              orderDirection: sorting?.type,
              request,
            })
              ?.unwrap()
              .then(result => {
                downloadFile(new File([result], 'Выборка.csv'));
              }),
          extraButtons: extraButtons,
        }}
        sortingConfig={{
          options: [
            { label: 'по дате', value: 'CREATED_AT' },
            { label: 'по ФИО', value: 'NAME' },
            { label: 'по сообщениям', value: 'UNREAD_MESSAGES_COUNT' },
          ],
        }}
        shouldOpenInNewTab
        filterSettingsPageKey="applications"
      />
    </>
  );
};

export default ModeratorStatementsPage;
